/**
 * src/shell/employee_shell.js
 *
 * The employee/supervisor self-service shell (EMPLOYEE_PORTAL_PLAN §2,
 * 2026-07-14). Forked into from shell.js's attemptBootstrapAndMount for a
 * definitively-resolved employee/supervisor caller. It reuses the SAME layout
 * skeleton (#app-shell / .sidebar / .main-area / .top-header / #content-region)
 * and the SAME chrome components as the admin cockpit — imported directly from
 * shell.js (populateUserChip / populateTenantLogo / wireLangToggle /
 * wireThemeToggle / loadNotifCount / …) so there is ONE implementation of each,
 * not a fork — differing only in:
 *   - a FLAT 6-item nav (employee_pillars.js) instead of the 5 labeled pillars,
 *   - each nav item routing straight to its screen (no springboard),
 *   - the account-menu Profile target (→ /my-more, not the admin /settings),
 *   - no top-bar command-palette search (employees don't need it),
 *   - default landing = Home (/me).
 *
 * Route ownership: the route table below is this shell's; Home (/me) mounts the
 * real employee_home.js composite, every other nav route mounts a placeholder
 * until the Wave-3 screens (my_attendance / my_leave / my_tasks / my_payslip /
 * more) are wired in. Reads are self-scoped by construction — every CF the
 * employee screens call resolves identity from the auth token server-side, so
 * an employee is structurally incapable of seeing another employee's data
 * (plan §7.3); this shell adds no data access of its own beyond the reused
 * populateUserChip / loadNotifCount (both already self-scoped).
 */

import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase.js';
import { registerRoute, onRouteChange, navigate, startRouter, getCurrentPath } from '../lib/router.js';
import { addLangListener } from '../i18n/store.js';
import { t } from '../i18n/t.js';
import { ICONS } from './pillars.js';
import { mountPlaceholder } from '../screens/placeholder.js';
import {
  EMPLOYEE_NAV,
  EMPLOYEE_HOME_PATH,
  isEmployeePath,
  employeeItemForPath,
} from './employee_pillars.js';
import {
  defaultLogoSrc,
  ICON_MOON,
  populateUserChip,
  populateTenantLogo,
  wireLangToggle,
  syncLangToggle,
  wireThemeToggle,
  loadNotifCount,
  closeMobileSidebar,
  mountDegradedSessionBanner,
} from './shell.js';

/**
 * mountEmployeeShellFrame — renders the self-service shell for an
 * employee/supervisor caller. `bootstrapFailed` mirrors shell.js: true only
 * when the (already-retried) bootstrap genuinely failed — surfaces the same
 * degraded-session banner.
 */
export function mountEmployeeShellFrame(root, user, role, bootstrapFailed = false) {
  root.innerHTML = employeeShellTemplate();

  if (bootstrapFailed) mountDegradedSessionBanner(root);

  renderEmployeeNav(root);

  // Mobile sidebar open/close — identical class contract (.sidebar/.open,
  // #sidebar-overlay/.visible, #sidebar-close, #menu-btn) as the admin shell,
  // so the existing responsive CSS applies verbatim.
  const sidebarEl = root.querySelector('.sidebar');
  const overlayEl = root.querySelector('#sidebar-overlay');
  root.querySelector('#menu-btn')?.addEventListener('click', () => {
    sidebarEl?.classList.add('open');
    overlayEl?.classList.add('visible');
  });
  const closeSidebar = () => closeMobileSidebar(root);
  root.querySelector('#sidebar-close')?.addEventListener('click', closeSidebar);
  overlayEl?.addEventListener('click', closeSidebar);

  wireEmployeeAccountMenu(root);

  // Notification bell → the notification centre (surfaced under "More" until
  // the Wave-3 notifications screen is wired into the route table). The count
  // badge is the SAME self-scoped getNotifications read the admin bell uses.
  root.querySelector('#header-notif-btn')?.addEventListener('click', () => navigate('/my-more'));
  loadNotifCount(root);

  wireLangToggle(root);
  wireThemeToggle(root);

  const routeTable = buildRouteTable();
  for (const [path, fn] of routeTable) registerRoute(path, fn);

  // LEAK FIX (2026-08-19): same per-mount unsub contract as shell.js's
  // _routeChangeUnsub — router.js's listener list is module-global, so a
  // re-mount without this stacked a closure retaining the detached shell
  // DOM. main.js's resetRouteListeners() covers cross-persona switches.
  if (_empRouteUnsub) _empRouteUnsub();
  _empRouteUnsub = onRouteChange((path) => updateEmployeeNav(root, path));

  // Default landing = Home; also redirect a stale/foreign hash (e.g. an admin
  // #/dashboard left in this tab from an earlier session) onto Home. Seed the
  // hash BEFORE startRouter so router.js's own no-hash branch never fires with
  // its /dashboard default — same technique shell.js uses (router.js untouched).
  const rawHash = window.location.hash;
  const currentPath = rawHash ? rawHash.slice(1) : '';
  if (!rawHash || !isEmployeePath(currentPath)) {
    window.location.hash = `#${EMPLOYEE_HOME_PATH}`;
  }

  startRouter(root.querySelector('#content-region'));

  // Async, never blocks the shell mount (mirrors the admin path).
  populateUserChip(root, user);
  populateTenantLogo(root);

  wireEmployeeLangReactivity(root, routeTable);
}

// ── Nav render + active-state ───────────────────────────────────────────

function renderEmployeeNav(root) {
  const navEl = root.querySelector('#sidebar-nav');
  if (!navEl) return;
  navEl.innerHTML = EMPLOYEE_NAV.map((item) => `
    <button type="button" class="labeled-rail-item" data-emp-nav="${escapeAttr(item.path)}">
      <span class="li-icon">${item.icon}</span>
      <span>${escapeHtml(t(item.labelKey))}</span>
    </button>`).join('');

  navEl.querySelectorAll('[data-emp-nav]').forEach((el) => {
    el.addEventListener('click', () => {
      navigate(el.dataset.empNav);
      closeMobileSidebar(root);
    });
  });
}

function updateEmployeeNav(root, path) {
  root.querySelectorAll('[data-emp-nav]').forEach((el) => {
    el.classList.toggle('active', el.dataset.empNav === path);
  });
  const item = employeeItemForPath(path);
  const titleEl = root.querySelector('#page-title');
  if (titleEl) titleEl.textContent = item ? t(item.labelKey) : 'Staffly';
}

// ── Route table ─────────────────────────────────────────────────────────

function buildRouteTable() {
  const table = new Map();
  table.set(EMPLOYEE_HOME_PATH, async (el) => (await import('../screens/employee_home.js')).mountEmployeeHome(el));
  // Wave-3 screens (real mounters, set explicitly BEFORE the placeholder
  // fallback loop below so its `table.has(item.path)` guard skips them).
  table.set('/my-attendance', async (el) => (await import('../screens/my_attendance.js')).mountMyAttendance(el));
  table.set('/my-leave', async (el) => (await import('../screens/my_leave.js')).mountMyLeave(el));
  table.set('/my-tasks', async (el) => (await import('../screens/my_tasks_requests.js')).mountMyTasksRequests(el));
  table.set('/my-payslip', async (el) => (await import('../screens/my_payslip.js')).mountMyPayslip(el));
  table.set('/my-more', async (el) => (await import('../screens/my_more.js')).mountMyMore(el));
  // Safety fallback: any employee nav path not explicitly wired above (e.g.
  // a future nav item added before its screen lands) gets an honest
  // placeholder so the shell stays fully navigable (t() is called INSIDE the
  // closure so the placeholder label re-translates on a language flip).
  for (const item of EMPLOYEE_NAV) {
    if (table.has(item.path)) continue;
    const { labelKey } = item;
    table.set(item.path, (el) => mountPlaceholder(el, t(labelKey)));
  }
  table.set('__notfound__', (el) => mountPlaceholder(el, t('employee.navHome')));
  return table;
}

// ── Account menu (Profile → /my-more, not the admin /settings) ──────────

let _empAccountAbort = null;

// Route-change unsub handle (see the LEAK FIX note in
// mountEmployeeShellFrame — mirrors _empLangUnsub below).
let _empRouteUnsub = null;

function wireEmployeeAccountMenu(root) {
  const avatarBtn = root.querySelector('#header-avatar');
  const menuEl = root.querySelector('#account-menu');
  const dropdownEl = root.querySelector('#account-dropdown');
  if (!avatarBtn || !menuEl || !dropdownEl) return;

  if (_empAccountAbort) _empAccountAbort.abort();
  _empAccountAbort = new AbortController();
  const { signal } = _empAccountAbort;
  const closeMenu = () => dropdownEl.classList.remove('open');

  avatarBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdownEl.classList.toggle('open');
  });
  document.addEventListener('click', (e) => {
    if (!menuEl.contains(e.target)) closeMenu();
  }, { signal });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  }, { signal });

  root.querySelector('#account-menu-profile')?.addEventListener('click', () => {
    closeMenu();
    navigate('/my-more');
  });
  root.querySelector('#account-menu-signout')?.addEventListener('click', () => {
    closeMenu();
    signOut(auth);
  });
}

// ── Language reactivity (re-render nav labels + active screen on flip) ──

let _empLangUnsub = null;

function wireEmployeeLangReactivity(root, routeTable) {
  if (_empLangUnsub) _empLangUnsub();
  _empLangUnsub = addLangListener(() => {
    syncLangToggle(root);
    renderEmployeeNav(root);
    updateEmployeeNav(root, getCurrentPath());
    reRenderActiveRoute(root, routeTable);
  });
}

function reRenderActiveRoute(root, routeTable) {
  const contentEl = root.querySelector('#content-region');
  if (!contentEl) return;
  const path = getCurrentPath();
  const fn = routeTable.get(path) || routeTable.get('__notfound__');
  contentEl.innerHTML = '';
  if (fn) fn(contentEl);
}

// ── Template ────────────────────────────────────────────────────────────

const ICON_HAMBURGER = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 12h18M3 6h18M3 18h18"/></svg>';

function employeeShellTemplate() {
  return `
    <div id="app-shell" class="visible">
      <div class="shell-body">
        <aside class="sidebar">
          <div class="sidebar-logo">
            <div class="sidebar-brand" id="sidebar-logo-text"><img id="sidebar-logo-default" src="${defaultLogoSrc()}" alt="" style="height:26px;width:auto;max-width:130px;object-fit:contain" /></div>
            <img id="sidebar-logo-img" alt="" style="display:none;max-height:28px;max-width:140px;width:auto;height:auto;object-fit:contain" />
            <button class="sidebar-close" id="sidebar-close" aria-label="${escapeAttr(t('common.close'))}">&times;</button>
          </div>
          <nav class="sidebar-nav" id="sidebar-nav"></nav>
          <div class="sidebar-user">
            <div class="user-avatar" id="sidebar-av">…</div>
            <div class="user-info">
              <div class="user-name" id="sidebar-name">…</div>
              <div class="user-role" id="sidebar-role">—</div>
            </div>
          </div>
        </aside>
        <div class="sidebar-overlay" id="sidebar-overlay"></div>
        <div class="main-area">
          <div class="top-header">
            <button class="menu-btn" id="menu-btn" aria-label="${escapeAttr(t('employee.openMenu'))}">${ICON_HAMBURGER}</button>
            <div class="page-title" id="page-title">Staffly</div>
            <div style="flex:1"></div>
            <button type="button" class="header-icon-btn" id="header-notif-btn" title="${escapeAttr(t('employee.notifications'))}" aria-label="${escapeAttr(t('employee.notifications'))}">
              ${ICONS.notifications}
              <span class="notif-count" id="header-notif-count" hidden></span>
            </button>
            <button type="button" class="header-icon-btn" id="theme-toggle" aria-label="${escapeAttr(t('employee.toggleTheme'))}">${ICON_MOON}</button>
            <button type="button" class="header-icon-btn header-lang-btn" id="lang-toggle" aria-label="${escapeAttr(t('employee.toggleLanguage'))}">AR</button>
            <div class="header-role-badge" id="header-role-badge">…</div>
            <div class="account-menu" id="account-menu">
              <button type="button" class="header-avatar" id="header-avatar" aria-haspopup="true" aria-label="${escapeAttr(t('employee.accountMenu'))}">…</button>
              <div class="account-dropdown" id="account-dropdown">
                <div class="account-dropdown-header">
                  <div class="account-dropdown-name" id="account-menu-name">…</div>
                  <div class="account-dropdown-email" id="account-menu-email"></div>
                </div>
                <button type="button" class="account-dropdown-item" id="account-menu-profile">${escapeHtml(t('employee.profile'))}</button>
                <div class="account-dropdown-sep"></div>
                <button type="button" class="account-dropdown-item danger" id="account-menu-signout">${escapeHtml(t('common.signOut'))}</button>
              </div>
            </div>
          </div>
          <div class="content" id="content-region"></div>
        </div>
      </div>
    </div>`;
}

// ── Local escaping helpers ──────────────────────────────────────────────

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
function escapeAttr(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
